import { WorkProduct, Feature, Initiative } from "../types/index";
import { generateId } from "../utils/helpers";
import { submitAndStreamJob, JobStreamUpdate } from "@/api/workProcessor";
import { setWorkProductReview } from "@/api/workproduct/documents";
import { toast } from "sonner";

/**
 * Generate a composite key for tracking work product generation state.
 * This ensures that work products with the same template ID across different features/user stories/products
 * are tracked separately.
 *
 * Format:
 * - User Story level: "us-{userStoryId}-wp-{workProductId}"
 * - Feature level: "f-{featureId}-wp-{workProductId}"
 * - Product level: "p-{projectId}-wp-{workProductId}"
 */
const generateWorkProductKey = (
    workProductId: string,
    projectId?: number | null,
    featureId?: number | string | null,
    userStoryId?: string | null
): string => {
    if (userStoryId) {
        return `us-${userStoryId}-wp-${workProductId}`;
    }
    if (featureId) {
        return `f-${featureId}-wp-${workProductId}`;
    }
    // Product level (no feature or user story) - include project ID to differentiate between products
    if (projectId) {
        return `p-${projectId}-wp-${workProductId}`;
    }
    // Fallback if no context is available (shouldn't happen in normal flow)
    return `wp-${workProductId}`;
};

export type GenerationScope = {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    featureId: number | null;
    userStoryId: string | null;
};

export type WorkProductGenerationSubmission =
    | { ok: true; jobId: string }
    | { ok: false; error: string };

export const resolveWorkProductSelection = (
    workProduct: WorkProduct,
    currentWorkProduct?: WorkProduct,
    explicitScope?: GenerationScope
): WorkProduct => {
    if (!explicitScope) return currentWorkProduct || workProduct;

    return {
        ...workProduct,
        level: explicitScope.level,
        feature_id: explicitScope.featureId ?? undefined,
        user_story_id: explicitScope.userStoryId ?? undefined
    };
};

const normalizeFeatureId = (feature: Feature | null): number | null => {
    const rawFeatureId = feature?.id || (feature as any)?.featureid;
    if (rawFeatureId == null) return null;
    const numeric = typeof rawFeatureId === "string" ? parseInt(rawFeatureId, 10) : Number(rawFeatureId);
    return Number.isNaN(numeric) ? null : numeric;
};

export const resolveGenerationScope = (
    workProduct: WorkProduct,
    selectedFeature: Feature | null,
    selectedUserStory: any | null,
    explicitScope?: GenerationScope
): GenerationScope => {
    if (explicitScope) return explicitScope;

    const declaredLevels = [workProduct.level, ...(workProduct.display_level || [])]
        .map(level => String(level || "").trim().toUpperCase())
        .filter(Boolean);
    const featureId = normalizeFeatureId(selectedFeature);
    const userStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : null;

    if (declaredLevels.includes("USERSTORY") && featureId && userStoryId) {
        return { level: "USERSTORY", featureId, userStoryId: featureId ? userStoryId : null };
    }
    if (declaredLevels.includes("FEATURE") && featureId) {
        return { level: "FEATURE", featureId, userStoryId: null };
    }
    if (declaredLevels.includes("PRODUCT")) {
        return { level: "PRODUCT", featureId: null, userStoryId: null };
    }

    if (featureId && userStoryId) {
        return { level: "USERSTORY", featureId, userStoryId };
    }
    if (featureId) {
        return { level: "FEATURE", featureId, userStoryId: null };
    }
    return { level: "PRODUCT", featureId: null, userStoryId: null };
};

// Define work products that should have parent-child relationships
const PARENT_CHILD_WORK_PRODUCTS = {
    "market-research": "Market Research",
    "stakeholder-insights": "Stakeholder Insights",
    personas: "Personas",
    "user-flows": "User Flow",
    "information-architecture": "Information Architecture"
};

// Check if work product should create children
const shouldCreateChildren = (workProduct: WorkProduct): boolean => {
    const isDiscoveryOrDesign = workProduct.phase === "discovery" || workProduct.phase === "design";
    const isParentWorkProduct = Object.keys(PARENT_CHILD_WORK_PRODUCTS).includes(workProduct.id);
    return isDiscoveryOrDesign && isParentWorkProduct;
};

// Create 4 child work products for parent
const createChildWorkProducts = (parentWorkProduct: WorkProduct): WorkProduct[] => {
    const parentName = PARENT_CHILD_WORK_PRODUCTS[parentWorkProduct.id as keyof typeof PARENT_CHILD_WORK_PRODUCTS];
    const children: WorkProduct[] = [];

    for (let i = 1; i <= 4; i++) {
        const childId = `${parentWorkProduct.id}-child-${i}`;
        const childName = `Focused ${parentName} ${i.toString().padStart(2, "0")}`;

        children.push({
            id: childId,
            name: childName,
            type: parentWorkProduct.type,
            phase: parentWorkProduct.phase,
            description: `Focused aspect ${i} of ${parentName}`,
            state: "editable",
            status: "available",
            approved: false,
            children: [],
            isChild: true,
            parentId: parentWorkProduct.id,
            hasContent: false
        });
    }

    return children;
};

// Simplified work product handlers with single-level structure
export const createWorkProductHandlers = (
    selectedFeature: Feature | null,
    selectedUserStory: any | null,
    initiatives: Initiative[],
    setSelectedWorkProduct: (workProduct: WorkProduct | null) => void,
    openWorkProductEditor: (workProduct: WorkProduct) => void,
    setLastUsedMatrixId: (id: string | null) => void,
    setGeneratingWorkProducts: React.Dispatch<React.SetStateAction<Set<string>>>,
    setProcessingWorkProducts: React.Dispatch<React.SetStateAction<Set<string>>>,
    setWorkProductProgress: React.Dispatch<React.SetStateAction<Map<string, any>>>,
    setWorkProductsByPhase: React.Dispatch<React.SetStateAction<Record<string, WorkProduct[]>>>,
    setExpandedWorkProducts: React.Dispatch<React.SetStateAction<Set<string>>>,
    workProductProgress: Map<string, any>,
    // Pass a getter function to always get current values instead of closure values
    getCurrentAuthData: () => { projectId: number | null; userId: number | null; authToken: string | null },
    // Add a getter to get current work products by phase
    getCurrentWorkProductsByPhase: () => Record<string, WorkProduct[]>
) => {
    // Enhanced handleAddWorkProductToEditable - generates content via streaming AND moves to editable
    const handleAddWorkProductToEditable = async (workProduct: WorkProduct, overrideProjectId?: number) => {
        // No feature selection required - we support Product, Feature, and User Story levels
        console.log("Adding work product to editable:", workProduct.id);
        console.log("Selected feature:", selectedFeature);
        console.log("Selected user story:", selectedUserStory);

        // Get current auth data - this ensures we always have the latest values
        console.log("Getting auth data from getCurrentAuthData()...");
        const authData = getCurrentAuthData();
        console.log("Auth data received:", authData);

        // Use override projectId if provided, otherwise try authData, then selectedFeature as fallback
        const { userId, authToken } = authData;
        const projectId = overrideProjectId || authData.projectId || (selectedFeature as any)?.projectid || null;

        // Check if we have required data for generation
        if (!projectId || !userId || !authToken) {
            console.error("VALIDATION FAILED - Missing required data!");
            console.error("  projectId:", projectId);
            console.error("  userId:", userId);
            console.error("  authToken:", authToken ? "EXISTS" : "MISSING");
            // This should never happen on the Feature Studio page
            toast.error("Unable to generate work product. Required product data is missing.");
            return;
        }

        console.log("VALIDATION PASSED - All required data present");

        // Convert workProduct.id to number if it's a string
        const workProductId = typeof workProduct.id === "string" ? parseInt(workProduct.id, 10) : workProduct.id;

        if (isNaN(workProductId)) {
            console.error("Invalid work product ID:", workProduct.id);
            toast.error("Invalid work product ID");
            return;
        }

        // Start processing animation immediately
        setProcessingWorkProducts(prev => new Set([...prev, workProduct.id]));

        try {
            // Debug: Check selectedUserStory
            console.log("=== JOB SUBMISSION DEBUG ===");
            console.log("selectedUserStory:", selectedUserStory);
            console.log("selectedUserStory?.id:", selectedUserStory?.id);

            // Submit job and start streaming
            const scope = resolveGenerationScope(workProduct, selectedFeature, selectedUserStory);
            console.log("Resolved generation scope (add-to-editable):", scope);
            const jobData: any = {
                workproductid: workProductId,
                projectid: projectId,
                userid: userId
            };

            // Respect work product level so product-level rebuilds do not inherit feature/story context.
            if (scope.level !== "PRODUCT" && scope.featureId) {
                jobData.featureid = scope.featureId;
            }

            if (scope.level === "USERSTORY" && scope.userStoryId) {
                jobData.user_story_id = scope.userStoryId;
            }

            // Pass force_new_version when rebuilding a work product
            if (workProduct.needs_rebuild) {
                jobData.force_new_version = true;
            }

            console.log("SUBMITTING JOB TO WORK PROCESSOR:", jobData);
            console.log("user_story_id value:", jobData.user_story_id);
            console.log("Making API call to /api/run-job...");

            // Submit job and start streaming - this updates the UI in real-time
            const { jobId, cancelStream } = await submitAndStreamJob(
                jobData,
                authToken,
                // onUpdate - real-time progress updates
                (update: JobStreamUpdate) => {
                    console.log("Job update:", update);

                    // Update progress indicator in real-time
                    if (update.progress !== undefined) {
                        // Parse section information from message if available
                        let currentSection: number | undefined;
                        let totalSections: number | undefined;

                        if (update.message) {
                            const sectionMatch = update.message.match(/section (\d+) of (\d+)/i);
                            if (sectionMatch) {
                                currentSection = parseInt(sectionMatch[1]);
                                totalSections = parseInt(sectionMatch[2]);
                            }
                        }

                        setWorkProductProgress(prev => {
                            const newProgress = new Map(prev);
                            newProgress.set(workProduct.id, {
                                progress: update.progress,
                                message: update.message || "Processing...",
                                current_section: currentSection || update.current_section,
                                total_sections: totalSections || update.total_sections
                            });
                            return newProgress;
                        });
                    }
                },
                // onComplete - when generation finishes, add to editable
                (result: JobStreamUpdate) => {
                    console.log("=== JOB COMPLETION HANDLER ===");
                    console.log("Job completed:", result);
                    console.log("Work product being processed:", workProduct);
                    console.log("Result data:", result.result || result.output || "No result data");

                    // Clear processing state
                    setProcessingWorkProducts(prev => {
                        console.log("Clearing processing state for:", workProduct.id);
                        const newSet = new Set(prev);
                        newSet.delete(workProduct.id);
                        return newSet;
                    });

                    // Clear progress
                    setWorkProductProgress(prev => {
                        console.log("Clearing progress for:", workProduct.id);
                        const newProgress = new Map(prev);
                        newProgress.delete(workProduct.id);
                        return newProgress;
                    });

                    console.log("Calling addWorkProductToEditableWithContent...");
                    // Add work product to editable with content
                    addWorkProductToEditableWithContent(workProduct, result.result || result.output);
                    console.log("=== JOB COMPLETION HANDLER END ===");
                },
                // onError
                (error: Error) => {
                    console.error("Job failed:", error);
                    toast.error(`Failed to generate work product: ${error.message}`);

                    // Clear processing state
                    setProcessingWorkProducts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(workProduct.id);
                        return newSet;
                    });

                    // Clear progress
                    setWorkProductProgress(prev => {
                        const newProgress = new Map(prev);
                        newProgress.delete(workProduct.id);
                        return newProgress;
                    });
                }
            );

            console.log("Job submitted with ID:", jobId);

            // Store the cancel function so we can cancel if needed
            setWorkProductProgress(prev => {
                const newProgress = new Map(prev);
                const currentProgress = prev.get(workProduct.id) || {};
                newProgress.set(workProduct.id, { ...currentProgress, cancel: cancelStream });
                return newProgress;
            });
        } catch (error) {
            console.error("Failed to submit job:", error);
            toast.error("Failed to generate work product");

            // Clear processing state
            setProcessingWorkProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(workProduct.id);
                return newSet;
            });
        }
    };

    // Function to add work product to editable area (UI only, no generation)
    const addWorkProductToEditableOnly = (workProduct: WorkProduct) => {
        setWorkProductsByPhase(prev => {
            const updated = { ...prev };
            const phaseWorkProducts = [...updated[workProduct.phase]];

            // Find and remove from Select area (available)
            const selectIndex = phaseWorkProducts.findIndex(wp => wp.id === workProduct.id && wp.state === "available");
            if (selectIndex !== -1) {
                phaseWorkProducts.splice(selectIndex, 1);
            }

            // Create parent work product
            const activeWorkProduct: WorkProduct = {
                ...workProduct,
                state: "editable",
                status: workProduct.status || "available", // Preserve status or default to 'available'
                approved: false,
                children: [],
                isChild: false
            };

            // Check if this work product should have children
            if (shouldCreateChildren(workProduct)) {
                console.log(`Creating parent-child relationship for: ${workProduct.name}`);

                // Create 4 child work products
                const childWorkProducts = createChildWorkProducts(workProduct);

                // Add child IDs to parent's children array
                activeWorkProduct.children = childWorkProducts.map(child => child.id);

                // Add parent to the beginning
                phaseWorkProducts.unshift(activeWorkProduct);

                // Add all children after the parent
                childWorkProducts.forEach(child => {
                    phaseWorkProducts.splice(1, 0, child);
                });

                console.log(
                    `Created parent "${activeWorkProduct.name}" with 4 children:`,
                    childWorkProducts.map(c => c.name)
                );
            } else {
                // Simple work product without children
                phaseWorkProducts.unshift(activeWorkProduct);
            }

            updated[workProduct.phase] = phaseWorkProducts;
            return updated;
        });

        console.log("Work product added to editable area:", workProduct.id);
    };

    // Helper function to add work product with generated content
    const addWorkProductToEditableWithContent = (workProduct: WorkProduct, content?: string) => {
        console.log("=== addWorkProductToEditableWithContent START ===");
        console.log("Work product to update:", workProduct);
        console.log("Content received:", content ? `${content.substring(0, 100)}...` : "No content");

        setWorkProductsByPhase(prev => {
            console.log("BEFORE update - workProductsByPhase:", JSON.parse(JSON.stringify(prev)));
            console.log(`BEFORE - ${workProduct.phase} phase work products:`, prev[workProduct.phase]);

            // Create a completely new object structure to ensure React detects the change
            const updated = { ...prev };

            // Create a new array for the phase to ensure React detects the change
            const phaseWorkProducts = prev[workProduct.phase].map(wp => {
                if (wp.id === workProduct.id) {
                    console.log("Updating work product:", wp);
                    // Return a completely new object for the matched work product
                    return {
                        ...wp,
                        state: "editable",
                        status: "completed",
                        hasContent: !!content,
                        content: content || wp.content,
                        user_story_id: selectedUserStory?.id || wp.user_story_id, // Preserve user_story_id context
                        level: selectedUserStory ? "USERSTORY" : selectedFeature ? "FEATURE" : "PRODUCT"
                    };
                }
                // Return the same reference for unchanged items
                return wp;
            });

            console.log("Phase work products after update:", phaseWorkProducts);

            // Assign the new array to trigger React's change detection
            updated[workProduct.phase] = phaseWorkProducts;

            console.log("AFTER update - workProductsByPhase:", JSON.parse(JSON.stringify(updated)));
            console.log(`AFTER - ${workProduct.phase} phase work products:`, updated[workProduct.phase]);

            // Check if we found and updated the work product
            const wasUpdated = phaseWorkProducts.some(wp => wp.id === workProduct.id && wp.state === "editable");

            if (wasUpdated) {
                return updated;
            }

            // If not found (shouldn't happen), create new one
            console.log("Work product not found, creating new one");
            const activeWorkProduct: WorkProduct = {
                ...workProduct,
                state: "editable",
                status: "completed",
                approved: false,
                children: [],
                isChild: false,
                hasContent: !!content,
                content: content,
                user_story_id: selectedUserStory?.id || workProduct.user_story_id, // Preserve user_story_id context
                level: selectedUserStory ? "USERSTORY" : selectedFeature ? "FEATURE" : "PRODUCT"
            };

            // Check if this work product should have children
            if (shouldCreateChildren(workProduct)) {
                console.log(`Creating parent-child relationship for: ${workProduct.name}`);

                // Create 4 child work products
                const childWorkProducts = createChildWorkProducts(workProduct);

                // Add child IDs to parent's children array
                activeWorkProduct.children = childWorkProducts.map(child => child.id);

                // Add parent to the beginning
                phaseWorkProducts.unshift(activeWorkProduct);

                // Add all children after the parent
                childWorkProducts.forEach(child => {
                    phaseWorkProducts.splice(1, 0, child);
                });

                console.log(
                    `Created parent "${activeWorkProduct.name}" with 4 children:`,
                    childWorkProducts.map(c => c.name)
                );
            } else {
                // Simple work product without children
                phaseWorkProducts.unshift(activeWorkProduct);
            }

            updated[workProduct.phase] = phaseWorkProducts;

            console.log("FINAL updated state being returned:", JSON.parse(JSON.stringify(updated)));
            return updated;
        });

        console.log("=== addWorkProductToEditableWithContent END ===");
        console.log("Work product added with content:", workProduct.id);
    };

    // Simplified handleWorkProductSelect - no child handling needed
    const handleWorkProductSelect = (workProduct: WorkProduct, explicitScope?: GenerationScope) => {
        console.log("Selecting work product:", workProduct);

        // Get the latest work product data from current state to ensure we have updated content
        const currentWorkProductsByPhase = getCurrentWorkProductsByPhase();
        const currentWorkProduct = currentWorkProductsByPhase[workProduct.phase]?.find(wp => wp.id === workProduct.id);

        console.log("Original work product:", workProduct);
        console.log("Current work product from state:", currentWorkProduct);

        // Optional SDD cards have an independent hierarchy selector. Preserve
        // that explicit scope instead of replacing the clicked item with the
        // same template from the main hierarchy's collection.
        const selectedWorkProduct = resolveWorkProductSelection(workProduct, currentWorkProduct, explicitScope);
        setSelectedWorkProduct(selectedWorkProduct);

        // Set last used matrix ID if we have a feature selected
        if (selectedFeature) {
            setLastUsedMatrixId(selectedFeature.id);
        }

        // Switch to editor view - works at Product, Feature, and User Story levels
        openWorkProductEditor(selectedWorkProduct);
    };

    const handleWorkProductGenerate = async (
        workProduct: WorkProduct,
        overrideProjectId?: number,
        options?: { forceNewVersion?: boolean; scope?: GenerationScope }
    ): Promise<WorkProductGenerationSubmission> => {
        console.log("Generating work product:", workProduct.id);
        console.log("Override projectId:", overrideProjectId);
        console.log("Selected feature:", selectedFeature);
        console.log("Selected user story:", selectedUserStory);

        // No feature selection required - we support Product, Feature, and User Story levels
        // The level is determined by what's selected (nothing = Product level, feature = Feature level, etc.)

        // Get current auth data - this ensures we always have the latest values
        const authData = getCurrentAuthData();
        // Use override projectId if provided, otherwise try authData, then selectedFeature as fallback
        const { userId, authToken } = authData;
        const projectId = overrideProjectId || authData.projectId || (selectedFeature as any)?.projectid || null;

        if (!projectId || !userId || !authToken) {
            console.error("Missing required data:", { projectId, userId, authToken: !!authToken });
            toast.error("Unable to generate work product: Missing required information");
            return { ok: false, error: "Unable to generate work product: missing required product or user information." };
        }

        // Convert workProduct.id to number if it's a string
        const workProductId = typeof workProduct.id === "string" ? parseInt(workProduct.id, 10) : workProduct.id;

        if (isNaN(workProductId)) {
            console.error("Invalid work product ID:", workProduct.id);
            toast.error("Invalid work product ID");
            return { ok: false, error: `Unable to generate ${workProduct.name}: its work-product ID is invalid.` };
        }

        // Generate composite key to track generation state uniquely per project/feature/user story
        const scope = options?.scope || resolveGenerationScope(workProduct, selectedFeature, selectedUserStory);
        const workProductKey = generateWorkProductKey(workProduct.id, projectId, scope.featureId, scope.userStoryId);
        console.log("Generated work product key for generation:", workProductKey);

        setGeneratingWorkProducts(prev => new Set([...prev, workProductKey]));

        // Set initial progress state immediately to show progress indicator
        setWorkProductProgress(prev => {
            const newProgress = new Map(prev);
            newProgress.set(workProductKey, {
                progress: 0,
                message: "Starting generation..."
            });
            return newProgress;
        });

        try {
            // Debug: Check selectedUserStory
            console.log("=== JOB SUBMISSION DEBUG (handleWorkProductGenerate) ===");
            console.log("selectedUserStory:", selectedUserStory);
            console.log("selectedUserStory?.id:", selectedUserStory?.id);

            // Submit job and start streaming updates
            const jobData: any = {
                workproductid: workProductId,
                projectid: projectId,
                userid: userId
            };
            console.log("Resolved generation scope (generate):", scope);

            // Respect work product level so product-level rebuilds do not inherit feature/story context.
            if (scope.level !== "PRODUCT" && scope.featureId) {
                jobData.featureid = scope.featureId;
            }

            if (scope.level === "USERSTORY" && scope.userStoryId) {
                jobData.user_story_id = scope.userStoryId;
            }

            // Pass force_new_version when rebuilding, or when an explicit
            // regenerate/new request came from chat tools.
            if (workProduct.needs_rebuild || options?.forceNewVersion) {
                jobData.force_new_version = true;
            }

            console.log("Submitting job to work processor:", jobData);
            console.log("user_story_id value:", jobData.user_story_id);

            // Submit job and start streaming
            const { jobId, cancelStream } = await submitAndStreamJob(
                jobData,
                authToken,
                // onUpdate - real-time progress updates
                (update: JobStreamUpdate) => {
                    console.log("Job update:", update);

                    // Update progress indicator
                    if (update.progress !== undefined) {
                        setWorkProductProgress(prev => {
                            const newProgress = new Map(prev);
                            newProgress.set(workProductKey, {
                                progress: update.progress,
                                message: update.message || "Processing..."
                            });
                            return newProgress;
                        });
                    }

                    // Handle streaming content updates if provided
                    if (update.result || update.output) {
                        setWorkProductsByPhase(prev => {
                            const updated = { ...prev };
                            const phaseWorkProducts = updated[workProduct.phase].map(wp =>
                                wp.id === workProduct.id
                                    ? {
                                          ...wp,
                                          hasContent: true,
                                          content: update.result || update.output
                                      }
                                    : wp
                            );
                            updated[workProduct.phase] = phaseWorkProducts;
                            return updated;
                        });
                    }
                },
                // onComplete
                (result: JobStreamUpdate) => {
                    console.log("Job completed:", result);

                    // Remove from processing set using composite key
                    setProcessingWorkProducts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(workProductKey);
                        return newSet;
                    });

                    // Remove from generating set using composite key
                    setGeneratingWorkProducts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(workProductKey);
                        return newSet;
                    });

                    // Clear progress using composite key
                    setWorkProductProgress(prev => {
                        const newProgress = new Map(prev);
                        newProgress.delete(workProductKey);
                        return newProgress;
                    });

                    // Update work product state to 'editable' and add content
                    setWorkProductsByPhase(prev => {
                        const updated = { ...prev };
                        const phaseWorkProducts = updated[workProduct.phase].map(wp =>
                            wp.id === workProduct.id
                                ? {
                                      ...wp,
                                      state: "editable" as const, // Change state from 'available' to 'editable'
                                      hasContent: true,
                                      content: result.result || result.output || "Work product generated successfully",
                                      user_story_id: scope.userStoryId || wp.user_story_id, // Preserve user story context when applicable
                                      level: scope.level
                                  }
                                : wp
                        );
                        updated[workProduct.phase] = phaseWorkProducts;
                        return updated;
                    });

                    toast.success("Work product generation completed");
                    console.log("Work product generation completed, moved to active list:", workProduct.id);
                },
                // onError
                (error: Error) => {
                    console.error("Job failed:", error);
                    toast.error(`Work product generation failed: ${error.message}`);

                    // Remove from processing set using composite key
                    setProcessingWorkProducts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(workProductKey);
                        return newSet;
                    });

                    // Remove from generating set using composite key
                    setGeneratingWorkProducts(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(workProductKey);
                        return newSet;
                    });

                    // Clear progress using composite key
                    setWorkProductProgress(prev => {
                        const newProgress = new Map(prev);
                        newProgress.delete(workProductKey);
                        return newProgress;
                    });
                }
            );

            if (jobId) {
                toast.success(`Work product generation started (Job ID: ${jobId})`);
                console.log("Job submitted with ID:", jobId);
                // Could store jobId and cancelStream for later use (e.g., for cancellation)
                return { ok: true, jobId };
            }
            return { ok: false, error: `The generation job for ${workProduct.name} could not be submitted.` };
        } catch (error) {
            console.error("Error generating work product:", error);
            toast.error(`Failed to generate work product: ${error instanceof Error ? error.message : "Unknown error"}`);

            // Generate the same composite key for cleanup
            const cleanupScope = options?.scope || resolveGenerationScope(workProduct, selectedFeature, selectedUserStory);
            const workProductKey = generateWorkProductKey(workProduct.id, projectId, cleanupScope.featureId, cleanupScope.userStoryId);

            // Remove from processing set on error using composite key
            setProcessingWorkProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(workProductKey);
                return newSet;
            });

            // Remove from generating set on error using composite key
            setGeneratingWorkProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(workProductKey);
                return newSet;
            });
            return {
                ok: false,
                error: `Failed to generate ${workProduct.name}: ${error instanceof Error ? error.message : "Unknown error"}`
            };
        }
    };

    // Enhanced expansion handler for parent-child relationships
    const handleToggleExpanded = (workProductId: string) => {
        console.log("Toggling expansion for work product:", workProductId);

        setExpandedWorkProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(workProductId)) {
                newSet.delete(workProductId);
                console.log(`Collapsed work product: ${workProductId}`);
            } else {
                newSet.add(workProductId);
                console.log(`Expanded work product: ${workProductId}`);
            }
            return newSet;
        });
    };

    // Persist review state for the latest generated version before reflecting
    // it in the UI. The backend remains authoritative across refreshes,
    // browsers, and reviewers.
    const handleApprovalToggle = async (workProduct: WorkProduct, approved: boolean, explicitScope?: GenerationScope): Promise<boolean> => {
        console.log("Toggling approval for:", workProduct.id, "to:", approved);

        const { projectId, authToken } = getCurrentAuthData();
        const numericWorkProductId = Number(workProduct.id);
        if (!projectId || !authToken || !Number.isInteger(numericWorkProductId) || numericWorkProductId <= 0) {
            toast.error("Unable to update the Work Product review. Required context is missing.");
            return false;
        }

        const scope = resolveGenerationScope(workProduct, selectedFeature, selectedUserStory, explicitScope);
        let persistedApproval: boolean;
        try {
            const review = await setWorkProductReview(
                {
                    projectId,
                    workproductId: numericWorkProductId,
                    accepted: approved,
                    featureId: scope.featureId,
                    userStoryId: scope.userStoryId
                },
                authToken
            );
            persistedApproval = review.approved;
        } catch (error) {
            toast.error(`Unable to update the Work Product review: ${error instanceof Error ? error.message : "Unknown error"}`);
            return false;
        }

        const selectedFeatureId = normalizeFeatureId(selectedFeature);
        const selectedStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : null;
        const currentCatalogScope: GenerationScope = selectedStoryId
            ? { level: "USERSTORY", featureId: selectedFeatureId, userStoryId: selectedStoryId }
            : selectedFeatureId
              ? { level: "FEATURE", featureId: selectedFeatureId, userStoryId: null }
              : { level: "PRODUCT", featureId: null, userStoryId: null };
        const belongsToCurrentCatalog =
            currentCatalogScope.level === scope.level &&
            currentCatalogScope.featureId === scope.featureId &&
            currentCatalogScope.userStoryId === scope.userStoryId;

        // This shared collection contains one hierarchy scope at a time. Do not
        // mark another scope's copy of a reusable template as accepted.
        if (belongsToCurrentCatalog) {
            setWorkProductsByPhase(prev => {
                const updated = { ...prev };

                Object.keys(updated).forEach(phase => {
                    updated[phase] = updated[phase].map(wp => {
                        if (wp.id === workProduct.id) {
                            return { ...wp, approved: persistedApproval };
                        }
                        if (wp.parentId === workProduct.id) {
                            return { ...wp, approved: persistedApproval };
                        }
                        return wp;
                    });
                });

                return updated;
            });
        }
        return true;
    };

    // Cancel work product progress
    const handleCancelWorkProductProgress = (workProductId: string) => {
        // Clear processing state
        setProcessingWorkProducts(prev => {
            const newSet = new Set(prev);
            newSet.delete(workProductId);
            return newSet;
        });

        // Clear progress
        setWorkProductProgress(prev => {
            const newProgress = new Map(prev);
            newProgress.delete(workProductId);
            return newProgress;
        });
    };

    return {
        handleWorkProductSelect,
        handleWorkProductGenerate,
        handleAddWorkProductToEditable,
        handleToggleExpanded,
        handleApprovalToggle,
        handleCancelWorkProductProgress
    };
};
